import { LINE_HARL, LINE_NAMES, LINE_SLOTS } from '@somnhold/content';
import { hexLen, hexToWorld, ROW, worldToHex, type Hex } from '../hex.ts';
import { idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';

/**
 * The Pass of the Rift (docs/sendas/guerrero.md, chapter 5): the grounds, the same from the seed in the sim and the
 * drawing, integers only. Up is −y (the top of the screen).
 *
 * - `cragLayout` (act 1, the ledge of the wind): a ledge two hexes wide (one in parts) winding up a cliff from the foot
 *   of the board to the pass's mouth at its top; the wall on the west (−x), the drop on the east, where the wind
 *   blows. Niches cut into the wall every few rows: there the wind doesn't strike.
 * - `passLayout` (acts 2 to 4, and the speech): the pass itself, a gorge seven lanes wide between two plates. Across
 *   its foot Ruy's line: eleven slots in a row, gently bowed, the fire at one end (slot 1) and the snow under Ruy's
 *   cornice at the other (slot 11; the seed mirrors it); Harl's banner behind the middle; the wedges' pile; the beam
 *   up the snow side's wall with its basket at its foot; Ruy's stone in the camp; and two rows of rocks up the pass,
 *   three of them with their grain for the avalanche the Ventisquero calls (`stormSlide`).
 * - Ruy's line, the puzzle (`lineClues`, `lineCheck`, `lineSolve`): the eight pages of the diary as rules over the
 *   eleven slots; `lineSolve` counts every way of setting the ten shields that keeps them all (one only).
 */

export const CRAG_RADIUS = 9;
export const PASS_RADIUS = 9;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const key = (h: Hex): string => `${h.q},${h.r}`;
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;

export interface CragLayout {
  radius: number;
  /** What can't be walked: the wall (west), the drop (east), the rock past the mouth. */
  rocks: Hex[];
  /** For the drawing: the wall's cells and the drop's. */
  wall: Hex[];
  drop: Hex[];
  /** The niches in the wall (open cells the wind misses), and the ledge's open cells. */
  niches: Hex[];
  ledge: Hex[];
  /** The ledge's middle, row by row from the foot to the mouth (sim units). */
  way: { x: number; y: number }[];
  /** The rows one hex wide. */
  narrow: number[];
  mouth: { x: number; y: number };
  start: { x: number; y: number };
}

/** The ledge's middle x at each row (from the foot, r = R − 1, up to the mouth, r = −(R − 1)). */
const cragMid = (seed: number): Map<number, number> => {
  const rng = seedRng(deriveSeed(seed, 0xc4a9));
  const R = CRAG_RADIUS;
  const out = new Map<number, number>();
  let x = rngRange(rng, -1, 1) * 500;
  let dir = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let r = R - 1; r >= -(R - 1); r--) {
    out.set(r, x);
    // It winds: a half hex a row one way for a few rows, then the other.
    if (rngInt(rng, 10) < 3) dir = -dir;
    x = imax(-2500, imin(2500, x + dir * 500));
    if (Math.abs(x) === 2500) dir = -dir;
  }
  return out;
};

/** The ledge of the wind from the seed. */
export const cragLayout = (seed: number): CragLayout => {
  const R = CRAG_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xc4aa));
  const mid = cragMid(seed);
  const narrow: number[] = [];
  // One hex wide in parts: two runs of two rows, from the seed (never at the foot or the mouth).
  for (const at of [rngRange(rng, 2, 5), rngRange(rng, -5, -2)]) narrow.push(at, at - 1);
  const open = new Set<string>();
  const ledge: Hex[] = [];
  const niches: Hex[] = [];
  const wall: Hex[] = [];
  const drop: Hex[] = [];
  const rocks: Hex[] = [];
  const nicheRows = new Set<number>();
  // A niche every third row or so, from the foot (never on a narrow row: the wall there is sheer).
  for (let r = R - 3; r >= -(R - 2); r -= 4) nicheRows.add(narrow.includes(r) ? r - 1 : r);
  // Each row's first cell (the wall's side of the ledge): the cell centre at or just west of the winding middle. A row
  // two wide has that cell and the next east; a narrow row, that one alone; a niche is the cell west of it.
  const first = (r: number): number => {
    const m = mid.get(r) as number;
    const off = (((r * 500) % 1000) + 1000) % 1000;
    return m - ((((m - off) % 1000) + 1000) % 1000);
  };
  for (const h of allCells(R)) {
    const m = mid.get(h.r);
    const p = hexToWorld(h.q, h.r);
    if (m === undefined) {
      rocks.push(h);
      // Past the mouth, the pass's ice; below the foot, scree.
      if (p.x > (mid.get(h.r > 0 ? R - 1 : -(R - 1)) ?? 0)) drop.push(h);
      else wall.push(h);
      continue;
    }
    const cx = first(h.r);
    if (p.x === cx || (p.x === cx + 1000 && !narrow.includes(h.r))) {
      open.add(key(h));
      ledge.push(h);
      continue;
    }
    if (p.x < cx) {
      if (nicheRows.has(h.r) && p.x === cx - 1000) {
        open.add(key(h));
        niches.push(h);
        continue;
      }
      wall.push(h);
    } else drop.push(h);
    rocks.push(h);
  }
  const way: { x: number; y: number }[] = [];
  for (let r = R - 1; r >= -(R - 1); r--) way.push({ x: first(r) + (narrow.includes(r) ? 0 : 500), y: r * ROW });
  rocks.sort(sortHex);
  wall.sort(sortHex);
  drop.sort(sortHex);
  niches.sort(sortHex);
  ledge.sort(sortHex);
  void open;
  const top = way[way.length - 1] as { x: number; y: number };
  const foot = way[0] as { x: number; y: number };
  return { radius: R, rocks, wall, drop, niches, ledge, way, narrow, mouth: top, start: { x: foot.x, y: foot.y } };
};

/* ---------- the pass ---------- */

export const PASS_LANES = 7;
export const PASS_LANE_W = 1300;
/** The row Ruy's line stands on; the rows of the avalanche's rocks. */
export const LINE_ROW = 7;
const SLIDE_ROWS = [3, 5];

/** A rock of the avalanche (its lane, its grain: +1 toward the higher lane). */
export interface PassRock {
  lane: number;
  dir: number;
  x: number;
  y: number;
  cell: Hex;
}

export interface PassLayout {
  radius: number;
  rocks: Hex[];
  /** The gorge's walls either side (for the drawing). */
  walls: Hex[];
  /** −1: the fire is west (slot 1 at −x); 1: east. */
  side: number;
  /** Ruy's line: its eleven slots (index 0 is slot 1, by the fire; index 5 is Harl's), and its y at the middle. */
  slots: { x: number; y: number }[];
  lineY: number;
  x0: number;
  laneW: number;
  fire: { x: number; y: number };
  /** Behind the middle: Harl's banner; the wedges' pile; the stretcher with the shields (act 2). */
  banner: { x: number; y: number };
  pile: { x: number; y: number };
  stretcher: { x: number; y: number };
  /** Ruy on his stone in the camp (act 2): his own shield by him. */
  ruy: { x: number; y: number };
  /** The beam up the snow side's wall: its foot (the basket, the stones), the cornice it holds over the pass. */
  beam: { x: number; y: number };
  stones: { x: number; y: number }[];
  cornice: { x: number; y: number };
  /** The avalanche (act 4): its rocks in two rows, each with its grain; the snow it brings, by lane; the answer. */
  slide: { rows: PassRock[][]; snow: number[]; solution: number[] };
  /** Where the camp's nightmares stand (act 2), where things come down the pass from (the top). */
  camp: { x: number; y: number }[];
  top: { x: number; y: number };
  /** Where the hero stands as each act begins: behind the line. */
  start: { x: number; y: number };
}

/** How the avalanche comes down (`snow` by lane at its top) with these wedges in: the snow by lane at the line. */
export const passFlow = (rows: { lane: number; dir: number }[][], snow: readonly number[], shut: ArrayLike<number>): number[] => {
  let g = snow.slice();
  let i = 0;
  for (const row of rows) {
    const out = g.slice();
    for (const rk of row) {
      const k = i++;
      if (shut[k] !== 1) continue;
      const n = g[rk.lane] as number;
      if (n === 0 || n > 2) continue;
      out[rk.lane] = (out[rk.lane] as number) - n;
      const to = rk.lane + rk.dir;
      if (to >= 0 && to < PASS_LANES) out[to] = (out[to] as number) + n;
    }
    g = out;
  }
  return g;
};

/** The line's lanes (the ones its slots stand in): snow reaching any of them takes shields. */
export const LINE_LANES = [1, 2, 3, 4, 5];
export const passClear = (g: ArrayLike<number>): boolean => LINE_LANES.every((l) => g[l] === 0);

type Slide = { rows: { lane: number; dir: number }[][]; snow: number[]; solution: number[] };
let SLIDES: Slide[] | null = null;

/**
 * Every avalanche the pass can have (worked out once): two lanes of snow over the line, three rocks with grain in two
 * rows, where exactly one way of wedging them keeps the snow off the line, and it takes two of the three: the third, with
 * them or alone, puts snow on it (docs: «dos bien elegidas sacan la nieve de la línea; la tercera la tira encima»).
 */
export const stormSlides = (): Slide[] => {
  if (SLIDES) return SLIDES;
  const out: Slide[] = [];
  const opts: { row: number; lane: number; dir: number }[] = [];
  for (let row = 0; row < 2; row++) for (let lane = 0; lane < PASS_LANES; lane++) for (const dir of [-1, 1]) opts.push({ row, lane, dir });
  for (let a = 1; a <= 5; a++)
    for (let b = a + 1; b <= 5; b++) {
      const snow = new Array<number>(PASS_LANES).fill(0);
      snow[a] = 1;
      snow[b] = 1;
      for (let i = 0; i < opts.length; i++)
        for (let j = i + 1; j < opts.length; j++)
          for (let k = j + 1; k < opts.length; k++) {
            const pick = [opts[i], opts[j], opts[k]] as { row: number; lane: number; dir: number }[];
            const rows: { lane: number; dir: number }[][] = [[], []];
            let ok = true;
            for (const o of pick) {
              const row = rows[o.row] as { lane: number; dir: number }[];
              if (row.some((x) => x.lane === o.lane)) ok = false;
              row.push({ lane: o.lane, dir: o.dir });
            }
            if (!ok || !(rows[0] as unknown[]).length || !(rows[1] as unknown[]).length) continue;
            const clears: number[][] = [];
            for (let m = 0; m < 8; m++) {
              const shut = [m & 1, (m >> 1) & 1, (m >> 2) & 1];
              if (passClear(passFlow(rows, snow, shut))) clears.push([0, 1, 2].filter((x) => shut[x] === 1));
            }
            if (clears.length === 1 && (clears[0] as number[]).length === 2) out.push({ rows, snow, solution: clears[0] as number[] });
          }
    }
  SLIDES = out;
  return out;
};

/** The avalanche's rocks from the seed: one of `stormSlides` (a test checks every one has its one answer). */
export const stormSlide = (seed: number): Slide => {
  const all = stormSlides();
  return all[rngInt(seedRng(deriveSeed(seed, 0x5711)), all.length)] as Slide;
};

/** The pass from the seed. */
export const passLayout = (seed: number): PassLayout => {
  const R = PASS_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x9a55));
  const side = rngInt(rng, 2) === 0 ? -1 : 1;
  const bow = rngRange(rng, 0, 3);
  const laneW = PASS_LANE_W;
  const x0 = -((PASS_LANES * laneW) >> 1);
  const lineY = LINE_ROW * ROW;
  const slots: { x: number; y: number }[] = [];
  for (let i = 0; i < LINE_SLOTS; i++) {
    const k = i - 5;
    // Slot 1 by the fire (`side`), slot 11 under the snow.
    slots.push({ x: -side * k * 620, y: lineY - bow * (25 - k * k) * 14 });
  }
  const sl = stormSlide(seed);
  const rows: PassRock[][] = sl.rows.map((row, i) => {
    const y = (SLIDE_ROWS[i] as number) * ROW;
    return row.map((rk) => {
      const x = x0 + rk.lane * laneW + (laneW >> 1);
      return { ...rk, x, y, cell: worldToHex(x, y) };
    });
  });
  const rockK = new Set(rows.flatMap((r) => r.map((k) => key(k.cell))));
  const half = (PASS_LANES * laneW) >> 1;
  const rocks: Hex[] = [];
  const walls: Hex[] = [];
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    if (rockK.has(key(h))) {
      rocks.push(h);
      continue;
    }
    if (Math.abs(p.x) > half + 250) {
      rocks.push(h);
      walls.push(h);
    }
  }
  rocks.sort(sortHex);
  walls.sort(sortHex);
  const beam = { x: -side * (half - 700), y: 3 * ROW };
  const camp: { x: number; y: number }[] = [];
  for (let i = 0; i < 12; i++) camp.push({ x: rngRange(rng, -5, 5) * 700, y: rngRange(rng, -3, 1) * ROW + rngRange(rng, -300, 300) });
  return {
    radius: R,
    rocks,
    walls,
    side,
    slots,
    lineY,
    x0,
    laneW,
    fire: { x: side * (half - 500), y: lineY + 500 },
    banner: { x: 0, y: lineY + 1100 },
    pile: { x: -side * 1700, y: lineY + 1000 },
    stretcher: { x: side * 1500, y: lineY + 1300 },
    ruy: { x: side * 1800, y: 1 * ROW },
    beam,
    stones: [0, 1, 2].map((i) => ({ x: beam.x + side * (500 + i * 550), y: beam.y + 900 + i * 120 })),
    cornice: { x: -side * 900, y: 0 },
    slide: { rows, snow: sl.snow, solution: sl.solution },
    camp,
    top: { x: 0, y: -8 * ROW },
    start: { x: 0, y: lineY + 1300 },
  };
};

/** The lane a point is in (−1 outside the pass). */
export const passLane = (x: number): number => {
  const x0 = -((PASS_LANES * PASS_LANE_W) >> 1);
  if (x < x0) return -1;
  const k = idiv(x - x0, PASS_LANE_W);
  return k >= PASS_LANES ? -1 : k;
};

/* ---------- Ruy's line, the puzzle ---------- */

/** The slot (1 to 11) of each name in Ruy's plan (docs §4): the answer the eight pages lead to. */
export const LINE_ANSWER: Record<(typeof LINE_NAMES)[number], number> = { tome: 1, sanne: 2, joss: 3, ari: 4, gerd: 5, ruy: 7, nim: 8, lisbet: 9, juana: 10, fermin: 11 };

/**
 * Whether an arrangement (the slot, 1 to 11, of each of the ten names in LINE_NAMES' order) keeps all eight pages:
 * 1. Harl in the middle (his slot, 6, stays his); 2. Tomé by the fire (1), Sanne beside him; 3. Fermín under the snow
 * (11), Juana with him; 4. Ruy beside Harl; 5. Nim beside Ruy, on the snow's side; 6. Gerd on Harl's other side; 7. Ari
 * with Gerd; 8. Lisbet between Nim and Juana.
 */
export const lineCheck = (at: readonly number[]): boolean => {
  const n = (id: (typeof LINE_NAMES)[number]): number => at[LINE_NAMES.indexOf(id)] as number;
  const next = (a: number, b: number): boolean => Math.abs(a - b) === 1;
  if (at.some((x) => x === LINE_HARL)) return false;
  if (n('tome') !== 1 || !next(n('sanne'), n('tome'))) return false;
  if (n('fermin') !== LINE_SLOTS || !next(n('juana'), n('fermin'))) return false;
  if (!next(n('ruy'), LINE_HARL)) return false;
  if (n('nim') !== n('ruy') + 1) return false;
  const gerd = n('gerd');
  if (!next(gerd, LINE_HARL) || gerd - LINE_HARL !== LINE_HARL - n('ruy')) return false;
  if (!next(n('ari'), gerd)) return false;
  const lo = imin(n('nim'), n('juana'));
  const hi = imax(n('nim'), n('juana'));
  return n('lisbet') > lo && n('lisbet') < hi;
};

/** Every way of setting the ten shields in the ten slots that keeps the eight pages (the puzzle's check: one only). */
export const lineSolve = (): number[][] => {
  const free = [1, 2, 3, 4, 5, 7, 8, 9, 10, 11];
  const out: number[][] = [];
  const at = new Array<number>(LINE_NAMES.length).fill(0);
  const used = new Array<boolean>(free.length).fill(false);
  const go = (i: number): void => {
    if (i === LINE_NAMES.length) {
      if (lineCheck(at)) out.push(at.slice());
      return;
    }
    for (let k = 0; k < free.length; k++) {
      if (used[k]) continue;
      used[k] = true;
      at[i] = free[k] as number;
      go(i + 1);
      used[k] = false;
    }
  };
  go(0);
  return out;
};

/** How many of the ten shields stand in their slot (`on[slot index]`: the name's index, −1 empty). */
export const lineRight = (on: readonly number[]): number => {
  let n = 0;
  for (let i = 0; i < on.length; i++) {
    const who = on[i] as number;
    if (who >= 0 && LINE_ANSWER[LINE_NAMES[who] as (typeof LINE_NAMES)[number]] === i + 1) n++;
  }
  return n;
};

/** The order the eight pages come in (from the seed): each page's number, 1 to 8. */
export const linePages = (seed: number): number[] => {
  const rng = seedRng(deriveSeed(seed, 0x9a6e));
  const p = [1, 2, 3, 4, 5, 6, 7, 8];
  for (let i = p.length - 1; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = p[i] as number;
    p[i] = p[j] as number;
    p[j] = t;
  }
  return p;
};

/** The ways of loading the basket (up to `slots` stones of these weights) to each total: the beam's check. */
export const beamWays = (stones: readonly number[], slots: number, total: number): number[][] => {
  const out: number[][] = [];
  const go = (from: number, left: number, sum: number, acc: number[]): void => {
    if (sum === total) out.push(acc.slice());
    if (left === 0 || sum >= total) return;
    for (let i = from; i < stones.length; i++) {
      acc.push(stones[i] as number);
      go(i, left - 1, sum + (stones[i] as number), acc);
      acc.pop();
    }
  };
  go(0, slots, 0, []);
  return out;
};
